import find from 'lodash/find';
import forEach from 'lodash/forEach';
import map from 'lodash/map';
import orderBy from 'lodash/orderBy';

import {
  ChannelOptionVariantPrice,
  ProductOptionVariant,
  ChannelProductCombination,
  ChannelProductOption,
} from '@crema/models/channelProduct/index';
import { getLocalStorage, LOCAL_STORAGE_NAMES } from '@libs/storage/src/lib/local';

export const getLocalizedValue = (values: Array<any>, langCode = 'KO'): string | null => {
  if (!values || values.length === 0) return null;
  const v = find(values, (v) => v.langCode === langCode);
  return v ? v.value : values[0].value;
};

export const getLocalizedName = (values: Array<any>, langCode = 'KO'): string | undefined => {
  if (!values || values.length === 0) return undefined;
  const v = find(values, (v) => v.langCode === langCode);
  return v ? v.name : values[0].name;
};

export type ProductLinkageOption = {
  disabled: boolean;
  optionDetailsValueId: number;
  value: string;
  image?: string;
  color?: string;
  id: number;
  parentId?: number;
  parentValueIds?: Array<number>;
};

export type ProductOptionValue = {
  name: string;
  category: string;
  values: ProductLinkageOption[];
  multipleSelection: boolean;
  optionId: number;
  fixOption: boolean;
};

export type ChannelProductCombinationTab = {
  productCombinationId: number;
  combinedProductId: number;
  name?: string;
  productOptions: ChannelProductOption[];
};

const getParentValueIds = (optionValues: Array<any>) => {
  return optionValues.map((optionValue) => optionValue.optionDetailsValue.id);
};

export const getChannelProductOption = (productOption: ChannelProductOption): ProductOptionValue => {
  const lang = getLocalStorage(LOCAL_STORAGE_NAMES.LANG);
  const { optionDetails, productLinkageOptions, multipleSelection, fixOption } = productOption;
  // console.log(productOption);

  const { optionType, localizedNames } = optionDetails;
  const { category } = optionType;

  const name =
    localizedNames?.find((i) => i.langCode === lang)?.name ||
    localizedNames?.find((i) => i.langCode === 'KO')?.name ||
    localizedNames?.[0]?.name;

  const values = map(productLinkageOptions, (option) => {
    const { image, optionDetailsValue, linkageOption, linkageOptionValues, id } = option;
    const { id: optionDetailsValueId, localizedValues, previewOptionValue } = optionDetailsValue;

    return image
      ? {
          id,
          optionDetailsValueId,
          value: getLocalizedValue(localizedValues),
          image,
          parentId: linkageOption?.optionDetails?.id,
          parentValueIds: linkageOptionValues?.length ? getParentValueIds(linkageOptionValues) : undefined,
        }
      : {
          id,
          optionDetailsValueId,
          value: getLocalizedValue(localizedValues),
          color: previewOptionValue,
          parentId: linkageOption?.optionDetails?.id,
          parentValueIds: linkageOptionValues?.length ? getParentValueIds(linkageOptionValues) : undefined,
        };
  }).filter(Boolean) as ProductLinkageOption[];

  return {
    name,
    category,
    values,
    multipleSelection,
    optionId: optionDetails.id,
    fixOption,
  };
};

export const getChannelProductTabs = (
  productCombinations: ChannelProductCombination[] | undefined,
): ChannelProductCombinationTab[] => {
  const values = map(productCombinations, (combination) => {
    const { productCombinationId, combinedProduct, productOptions } = combination;
    const { id, localizedNames } = combinedProduct;

    return {
      productCombinationId,
      combinedProductId: id,
      name: getLocalizedName(localizedNames),
      productOptions,
    };
  });

  return values;
};

//
export const getChannelProductVariants = (
  optionNames: string[],
  optionVariants: ProductOptionVariant[],
  optionsMap: Map<number, string>,
  channelOptionVariantPrices: ChannelOptionVariantPrice[] = [],
) => {
  const rows: [string | number][] = [];

  forEach(optionVariants, (variant) => {
    const { id, optionVariantValues } = variant;
    const sortedValues = orderBy(optionVariantValues, ['productOptionId'], ['asc']);

    const channelOptionVariantPrice = find(channelOptionVariantPrices, (p) => p.optionVariantId === id);

    const row: [string | number] = [id];

    forEach(sortedValues, (value) => {
      const { productOptionValueIds } = value;
      const optionValue = map(productOptionValueIds, (id: number) => optionsMap.get(id)).join(', ');
      row.push(optionValue);
    });
    row[optionNames.length + 1] = channelOptionVariantPrice?.price || '';

    rows.push(row);
  });

  return rows;
};
